import * as React from 'react';
import { cn } from '../../lib/cn.js';

export interface PageHeaderProps {
  title: string;
  description?: React.ReactNode;
  /** Primary and secondary actions (right side on desktop). */
  actions?: React.ReactNode;
  /** Small row above the title (breadcrumb / back link). */
  eyebrow?: React.ReactNode;
  /** Status badges next to the title. */
  badges?: React.ReactNode;
  className?: string;
}

export const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(function PageHeader(
  { title, description, actions, eyebrow, badges, className },
  ref,
) {
  return (
    <div ref={ref} className={cn('mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between', className)}>
      <div className="grid min-w-0 gap-1">
        {eyebrow ? <div className="text-meta text-text-secondary">{eyebrow}</div> : null}
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-title font-semibold tracking-[-0.01em] text-text-primary">{title}</h1>
          {badges}
        </div>
        {description ? <div className="max-w-3xl text-body text-text-secondary">{description}</div> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
});

export interface SectionHeaderProps {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  level?: 2 | 3;
  className?: string;
  id?: string;
}

/** Heading for a group of content within a page. */
export function SectionHeader({ title, description, actions, level = 2, className, id }: SectionHeaderProps) {
  const H = level === 2 ? 'h2' : 'h3';
  return (
    <div className={cn('mb-3 flex flex-wrap items-end justify-between gap-3', className)}>
      <div className="grid gap-0.5">
        <H id={id} className="text-section font-semibold text-text-primary">
          {title}
        </H>
        {description ? <p className="text-body text-text-secondary">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
